<script setup>
import { format, isValid, parse, parseISO } from 'date-fns'
import { usePage } from '@inertiajs/vue3'
import { storeToRefs } from 'pinia'
import { useConfirm } from 'primevue/useconfirm'
import { computed, onBeforeUnmount, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFeature } from '@/composables/useFeature'
import { useHelpers } from '@/composables/useHelpers'
import { soldBuildingPart } from '@/services/soldBuildingPart'
import { useTransactionStore } from '@/stores/transaction'
import {
  TRANSACTION_INFO_FOTOLADU,
  TRANSACTION_INFO_GOOGLE_SEARCH,
  TRANSACTION_INFO_GOOGLE_SEARCH_KV,
  TRANSACTION_INFO_GOOGLE_SEARCH_KV_MAP,
  TRANSACTION_INFO_LINKS,
  TRANSACTION_KASI_F_FIELDS,
  TRANSACTION_KASI_T_FIELDS,
  TRANSACTION_KOM_FIELDS,
} from '../../../constants'
import { formatLink, normalizeUrl, validURL } from '../../../services/utilities'
import AdArchiveDialog from '../AdArchiveDialog.vue'
import RelatedTransactionsTableModal from '../RelatedTransactionsTableModal.vue'

const { t } = useI18n()
const confirm = useConfirm()
const page = usePage()
const canEditTransaction = computed(() => page.props.auth?.can?.editTransaction ?? false)
const isAdmin = computed(() => page.props.auth?.can?.admin ?? false)

const props = defineProps({
  transaction: {
    type: Object,
    required: true,
  },
})

const transactionStore = useTransactionStore()

const { formatTransactionValue, isKasiLikeTransaction, isKomLikeTransaction } =
  useHelpers()

const transactionData = ref({ ...props.transaction })

// aol_kpv arrives as dd.MM.yyyy from the API, but as yyyy-MM-dd when the
// transaction is re-served from the store cache after a sidebar save (the
// normalization below is written back through updateTransaction) — so accept
// both instead of throwing RangeError on reopen.
const parseAolDate = value => {
  if (!value) {
    return null
  }
  const api = parse(value, 'dd.MM.yyyy', new Date())
  if (isValid(api)) {
    return api
  }
  const iso = parseISO(value)
  return isValid(iso) ? iso : null
}

const aolDate = ref(parseAolDate(props.transaction.aol_kpv))

// Normalize transactionData.aol_kpv to yyyy-MM-dd so PUT requests use correct format
if (transactionData.value.aol_kpv) {
  transactionData.value.aol_kpv = aolDate.value
    ? format(aolDate.value, 'yyyy-MM-dd')
    : null
}

const building = ref({})
const fields = ['label', 'value']
const modalAdLink = ref(false)
const showRelatedTransactionsModal = shallowRef(false)

// RED-278: listing archive. The summary rides the details payload
// (transaction.ad_archive); the dialog refreshes it, and a link save marks it
// pending locally because the backend queues archiving on that same PUT.
const adArchiveEnabled = useFeature('ad-archive')
const showAdArchive = shallowRef(false)
const adArchiveSummary = ref(props.transaction.ad_archive ?? { status: 'none', archived_at: null, matches_current_link: null, versions_count: 0 })
const adArchiveStatus = computed(() => adArchiveSummary.value?.status ?? 'none')
const isArchivablePortal = url => /^https?:\/\/([a-z0-9-]+\.)*kv\.ee(\/|$)/i.test(url ?? '')
const formatArchiveDate = iso => (iso ? format(parseISO(iso), 'dd.MM.yyyy') : '')
const onAdArchiveUpdated = summary => {
  adArchiveSummary.value = summary
  transactionStore.setAdArchiveSummary(props.transaction.id, summary)
}
watch(
  () => props.transaction.ad_archive,
  summary => {
    if (summary) adArchiveSummary.value = summary
  },
)

// While a run is queued the line would otherwise show "ootel" until the next
// reopen — poll the archive state gently (a run takes ~20-70 s) and stop as
// soon as it settles. Cleared on unmount / transaction switch.
let adArchivePollTimer = null
const stopAdArchivePoll = () => {
  if (adArchivePollTimer) {
    clearTimeout(adArchivePollTimer)
    adArchivePollTimer = null
  }
}
const scheduleAdArchivePoll = () => {
  stopAdArchivePoll()
  if (adArchiveEnabled.value && adArchiveStatus.value === 'pending') {
    adArchivePollTimer = setTimeout(pollAdArchive, 10000)
  }
}
const pollAdArchive = async () => {
  const transactionId = props.transaction.id
  try {
    const data = await transactionStore.fetchAdArchive(transactionId)
    if (transactionId !== props.transaction.id) return
    onAdArchiveUpdated({
      status: data.status,
      archived_at: data.latest?.archived_at ?? null,
      matches_current_link: data.latest ? data.latest.matches_current_link : null,
    })
  } catch {
    // transient — keep polling while still pending
  } finally {
    // Re-arm from here, not from the status watcher: a tick that still sees
    // `pending` (or a failed request) must schedule the next one.
    scheduleAdArchivePoll()
  }
}
watch([adArchiveStatus, () => props.transaction.id], scheduleAdArchivePoll, { immediate: true })
onBeforeUnmount(stopAdArchivePoll)
const relatedTransactionsModal = useTemplateRef('relatedTransactionsModal')
const rows = ref([])
const links = {
  googleKv: TRANSACTION_INFO_GOOGLE_SEARCH_KV,
  google: TRANSACTION_INFO_GOOGLE_SEARCH,
  fotoladu: [
    {
      href: TRANSACTION_INFO_FOTOLADU,
      label: 'messages.transactions.landboard.kaldfotode_rakendus',
    },
  ],
}

const normalizedAdLink = computed(() => normalizeUrl(transactionData.value?.ad_link))

const linkState = computed(() => {
  return (
    !!normalizedAdLink.value &&
    normalizedAdLink.value.length >= 10 &&
    validURL(normalizedAdLink.value)
  )
})

// error shown only once the user has typed something — empty input is not "invalid"
const showLinkError = computed(
  () => !!transactionData.value?.ad_link?.trim() && !linkState.value,
)

const columns = computed(() => {
  if (!transactionData.value) {
    return []
  }
  if (isKomLikeTransaction(transactionData.value)) {
    return TRANSACTION_KOM_FIELDS
  } else if (
    isKasiLikeTransaction(transactionData.value) &&
    transactionData.value.hoonestatud === 1
  ) {
    return TRANSACTION_KASI_T_FIELDS
  } else if (
    isKasiLikeTransaction(transactionData.value) &&
    transactionData.value.hoonestatud === 0
  ) {
    return TRANSACTION_KASI_F_FIELDS
  }
})

const formatField = (field, transactionElement) => {
  return formatTransactionValue(field, transactionElement)
}

const createKatastriTunnusedLink = row => {
  const katastritunnused = row.split('|').join(';')
  return formatLink(TRANSACTION_INFO_LINKS['katastritunnused'], {
    katastritunnused,
  })
}

const googleLinks = computed(() => {
  return links.google.filter(link => formatLocal(link, transactionData.value))
})

const googleKvLinks = computed(() => {
  return links.googleKv.filter(link => formatLocal(link, transactionData.value))
})

const formatLocal = (link, data) => {
  if (!link) {
    return false
  }
  const dataClone = { ...data }

  if (dataClone?.buildings?.length > 0) {
    dataClone.building = dataClone.buildings[0]
  }

  // RED-355: the flat the sale sold, not the lowest-id part linked to it.
  const buildingPart = soldBuildingPart(dataClone)
  if (buildingPart) {
    dataClone.buildingPart = buildingPart
  }

  const intersectingKeys = Object.keys(dataClone).filter(key =>
    Object.keys(TRANSACTION_INFO_GOOGLE_SEARCH_KV_MAP).includes(key),
  )
  intersectingKeys.forEach(key => {
    TRANSACTION_INFO_GOOGLE_SEARCH_KV_MAP[key].forEach(test => {
      dataClone[key] = dataClone[key].replace(test.find, test.to)
    })
  })

  //if type set and limited to specific objet type, hide the link
  if ('type' in link && link.type != dataClone.objekti_liik) {
    return false
  }

  //try parsing the link, return false in case there is not enough data available (reference error occurs)
  try {
    return formatLink(link.href, dataClone)
  } catch (e) {
    return false
  }
}

const formatGeoLink = (link, data) => {
  if (!data.geometry) {
    return false
  }

  const coords = data.geometry.geometries[0].coordinates
  return formatLink(link, {
    x: coords[0],
    y: coords[1],
  })
}

const addLink = e => {
  e.preventDefault()

  transactionData.value.ad_link = normalizedAdLink.value
  const linkChanged = normalizedAdLink.value !== props.transaction.ad_link
  updateTransaction({
    ad_link: normalizedAdLink.value,
    ad_link_not_found: false
  }).then(() => {
    modalAdLink.value = false
    transactionData.value.ad_link_not_found = false
    if (adArchiveEnabled.value && linkChanged) {
      onAdArchiveUpdated(
        isArchivablePortal(normalizedAdLink.value)
          ? { ...adArchiveSummary.value, status: 'pending', matches_current_link: false }
          : { ...adArchiveSummary.value, status: 'unsupported', matches_current_link: false },
      )
    }
  })
}

const openAdLinkModal = () => {
  modalAdLink.value = true
}

const openRelatedTransactionsModal = notari_tehingu_nr => {
  showRelatedTransactionsModal.value = true
}

const changeMarketStatus = not_vabaturu_tehing => {
  updateTransaction({ not_vabaturu_tehing })
}

const changeAdLinkNotFound = ad_link_not_found => {
  const payload = { ad_link_not_found }
  if (ad_link_not_found) {
    payload.ad_link = null
  }
  transactionData.value.ad_link_not_found = ad_link_not_found
  updateTransaction(payload)
}

const onAolDateSelect = (date) => {
  aolDate.value = date
  const formatted = format(date, 'yyyy-MM-dd')
  transactionData.value.aol_kpv = formatted
  updateTransaction({ aol_kpv: formatted })
}

const onAolDateClear = () => {
  aolDate.value = null
  transactionData.value.aol_kpv = null
  updateTransaction({ aol_kpv: null })
}

const deleteAdLink = () => {
  confirm.require({
    message: t('messages.transactions.ad_link.delete_confirm'),
    header: t('messages.transactions.ad_link.delete_confirm_header'),
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => {
      updateTransaction({ ad_link: null })
    },
  })
}

const updateTransaction = changes => {
  return new Promise((resolve, reject) => {
    const merged = {
      ...props.transaction,
      ...transactionData.value,
      ...changes,
    }
    try {
      transactionStore.updateTransaction(merged)

      resolve()
    } catch (error) {
      reject(error)
    }
  })
}
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full border-collapse">
      <tbody>
        <tr v-for="column in columns" :key="column" class="border-b border-gray-200 dark:border-gray-700 even:bg-surface-50 dark:odd:bg-surface-900">
          <td class="py-2 px-4 w-2/5">{{ $t(`messages.transactions.fields.${column}`) }}</td>
          <td class="py-2 px-3">
            <a
              v-if="column === 'katastritunnused'"
              :href="createKatastriTunnusedLink(transactionData[column])"
              target="_blank"
              class="text-primary font-light hover:text-primary-600"
            >
              <span
                v-for="code in transactionData[column].split('|')"
                :key="code"
                class="block"
              >
                {{ code }}
              </span>
            </a>
            <a
              v-else-if="
                column === 'notari_tehingu_nr' &&
                transactionData[column]
              "
              href="#"
              @click.prevent="openRelatedTransactionsModal(transactionData[column])"
              class="text-primary hover:text-primary-600"
            >
              <span class="block">{{ transactionData[column] }}</span>
            </a>
            <template v-else-if="column === 'aol_kpv'">
              <DatePicker
                v-if="canEditTransaction"
                v-model="aolDate"
                date-format="dd.mm.yy"
                showIcon
                showClear
                iconDisplay="input"
                :placeholder="$t('messages.transactions.add_date')"
                @date-select="onAolDateSelect"
                @update:model-value="(val) => { if (val === null) onAolDateClear() }"
                fluid
                data-testid="aol-date-picker"
              />
              <span v-else>{{ formatField(column, transactionData[column]) }}</span>
            </template>
            <span v-else>{{ formatField(column, transactionData[column]) }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="px-4">
    <div class="mt-4 mb-2 px-1 flex items-center gap-2">
      {{ $t('messages.transactions.not_free_market') }}
      <ToggleSwitch
        v-if="canEditTransaction"
        v-model="transactionData.not_vabaturu_tehing"
        @update:model-value="changeMarketStatus"
      />
      <span v-else class="text-sm text-gray-500">{{ transactionData.not_vabaturu_tehing ? 'Jah' : 'Ei' }}</span>
    </div>
    <div class="p-1">
      <strong class="mb-2 mt-3 flex justify-between">
        <span>{{ $t('messages.transactions.ad_link.link') }}</span>
        <div v-if="canEditTransaction" class="flex items-center gap-2">
          <a href="#" class="text-primary hover:text-primary-600" data-testid="ad-link-modal-open" @click.prevent="openAdLinkModal">
            {{ $t(`messages.transactions.ad_link.${transaction.ad_link ? 'edit' : 'add'}`) }}
          </a>
          <a
            v-if="transaction.ad_link"
            href="#"
            class="text-red-600 hover:text-red-700 whitespace-nowrap"
            @click.prevent="deleteAdLink"
          >
            {{ $t('messages.transactions.ad_link.delete') }}
          </a>
        </div>
      </strong>
      <ul v-if="transaction.ad_link">
        <li class="flex items-center justify-between gap-2">
          <a
            :href="normalizeUrl(transaction.ad_link)"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary hover:text-primary-600 flex-1 break-all"
            data-testid="ad-link-anchor"
          >
            <span class="pi pi-external-link"></span>
            {{ transaction.ad_link }}
          </a>
        </li>
        <!-- RED-278: one reserved-height archive status box (prototype: grey card, action right-aligned); never shifts layout between states -->
        <li
          v-if="adArchiveEnabled"
          class="mt-2 min-h-[42px] rounded border border-surface-200 bg-surface-50 px-3 py-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1"
          data-testid="ad-archive-status-line"
        >
          <template v-if="adArchiveStatus === 'archived' || (adArchiveStatus === 'not_found' && adArchiveSummary.archived_at)">
            <span class="flex flex-wrap items-center gap-x-2">
              <i class="pi pi-box text-xs text-green-600"></i>
              <span>{{ $t('messages.transactions.ad_link.archive_copy_at', { date: formatArchiveDate(adArchiveSummary.archived_at) }) }}</span>
              <span v-if="adArchiveSummary.versions_count > 1" class="text-muted-color">
                · {{ $t('messages.transactions.ad_link.archive_versions_count', { n: adArchiveSummary.versions_count }) }}
              </span>
              <span v-if="adArchiveSummary.matches_current_link === false" class="text-muted-color">{{ $t('messages.transactions.ad_link.archive_from_earlier') }}</span>
            </span>
            <a href="#" class="ml-auto text-primary hover:text-primary-600" data-testid="ad-archive-open" @click.prevent="showAdArchive = true">
              {{ $t('messages.transactions.ad_link.archive_open') }}
            </a>
          </template>
          <template v-else-if="adArchiveStatus === 'pending'">
            <span class="text-muted-color"><i class="pi pi-box text-xs animate-pulse"></i> {{ $t('messages.transactions.ad_link.archive_pending') }}</span>
            <a href="#" class="ml-auto text-primary hover:text-primary-600" data-testid="ad-archive-open" @click.prevent="showAdArchive = true">
              {{ $t('messages.transactions.ad_link.archive_details') }}
            </a>
          </template>
          <template v-else-if="adArchiveStatus === 'failed' || adArchiveStatus === 'not_found'">
            <span class="text-amber-700"><i class="pi pi-exclamation-triangle text-xs text-amber-600"></i>
              {{ $t(`messages.transactions.ad_link.${adArchiveStatus === 'failed' ? 'archive_failed' : 'archive_not_found'}`) }}
            </span>
            <a href="#" class="ml-auto text-primary hover:text-primary-600" data-testid="ad-archive-open" @click.prevent="showAdArchive = true">
              {{ $t('messages.transactions.ad_link.archive_details') }}
            </a>
          </template>
          <template v-else-if="adArchiveStatus === 'unsupported'">
            <span class="text-muted-color"><i class="pi pi-ban text-xs"></i> {{ $t('messages.transactions.ad_link.archive_unsupported') }}</span>
          </template>
          <template v-else>
            <span class="text-muted-color"><i class="pi pi-box text-xs"></i> {{ $t('messages.transactions.ad_link.archive_none') }}</span>
            <a v-if="isAdmin" href="#" class="ml-auto text-primary hover:text-primary-600" data-testid="ad-archive-open" @click.prevent="showAdArchive = true">
              {{ $t('messages.transactions.ad_link.archive_now') }}
            </a>
          </template>
        </li>
      </ul>

      <span class="text-muted" :class="{ 'text-gray-400': transactionData.ad_link_not_found }" v-else>
        {{ $t('messages.transactions.ad_link.missing') }}
      </span>

      <div v-if="canEditTransaction" class="mt-3 flex items-center gap-2">
        {{ $t('messages.transactions.ad_link.not_found') }}
        <ToggleSwitch
          :model-value="transactionData.ad_link_not_found"
          @update:model-value="changeAdLinkNotFound"
        />
      </div>

      <strong class="block mt-3">
        {{ $t('messages.transactions.landboard.fotoladu') }}
      </strong>
      <ul>
        <li v-for="link in links.fotoladu" :key="link.href">
          <a
            :href="formatGeoLink(link.href, transactionData)"
            target="_blank"
            class="text-primary hover:text-primary-600"
          >
            <span class="pi pi-external-link"></span>
            {{ $t(link.label) }}
          </a>
        </li>
      </ul>

      <strong v-if="googleKvLinks.length > 0" class="block mt-3">
        {{ $t('messages.transactions.google.search.kv_ee') }}
      </strong>
      <ul>
        <li v-for="link in googleKvLinks" :key="link.href">
          <a
            :href="formatLocal(link, transactionData)"
            target="_blank"
            class="text-primary hover:text-primary-600"
          >
            <span class="pi pi-external-link"></span>
            {{ $t(link.label) }}
          </a>
        </li>
      </ul>

      <strong v-if="googleLinks.length > 0" class="block mt-3">
        {{ $t('messages.transactions.google.search.google') }}
      </strong>
      <ul class="mb-3">
        <li v-for="link in googleLinks" :key="link.href">
          <a
            :href="formatLocal(link, transactionData)"
            target="_blank"
            class="text-primary hover:text-primary-600"
          >
            <span class="pi pi-external-link"></span>
            {{ $t(link.label) }}
          </a>
        </li>
      </ul>
    </div>
  </div>
  <Dialog
    v-model:visible="modalAdLink"
    centered
    :header="$t('messages.transactions.ad_link.link')"
    :style="{ width: '30rem' }"
  >
    <div v-focustrap>
      <div class="mb-4">
        <label for="ad-link-input" class="block mb-1 font-medium">
          {{ $t('messages.transactions.enter.url_to_ad') }}
        </label>
        <InputText
          id="ad-link-input"
          v-model="transactionData.ad_link"
          :invalid="showLinkError"
          autofocus
          fluid
        />
        <small class="block mt-1 text-gray-500">
          {{ $t('messages.transactions.ad_link.hint') }}
        </small>
        <small
          class="block mt-1 text-red-600"
          :class="{ invisible: !showLinkError }"
          role="alert"
        >
          {{ $t('messages.transactions.enter.valid_url') }}&nbsp;
        </small>
      </div>
      <div class="flex justify-end gap-2">
        <Button
          type="button"
          label="Katkesta"
          severity="secondary"
          @click="modalAdLink = false"
        ></Button>
        <Button type="button" label="Salvesta" @click="addLink" :disabled="!linkState"></Button>
      </div>
    </div>
  </Dialog>
  <related-transactions-table-modal
    v-model:visible="showRelatedTransactionsModal"
    ref="relatedTransactionsModal"
    :transaction="transactionData"
  />
  <ad-archive-dialog
    v-if="adArchiveEnabled"
    v-model:visible="showAdArchive"
    :transaction-id="transaction.id"
    @updated="onAdArchiveUpdated"
  />
</template>

<style scoped lang="postcss"></style>
